// Please read CONFDEV-26277 for the whole context.
// By using AMD, we unveiled CQ-1043 in CQ so we need to delay the AMD
// bit until CQ-1043 gets resolved

// Facades and helpers
import { trigger } from '@confluence-classic/confluence-event_DO_NOT_USE';
import { isDarkFeatureEnabled_DO_NOT_USE } from '@confluence-classic/confluence-feature-flags';
import { getBootstrapManager } from '../facades/editor';
import { getOverride } from '@confluence/meta';
import window from 'window-or-global';

// Constants
import { CONTEXT_PATH } from '@confluence-classic/confluence-urls';

// Legacy editor bootstrapping
import { debugTime, debugTimeEnd } from './performance-session';
import { AtlassianTheme } from './theme-atlassian';
import Profiles from './profiles';

const HOST = `//${window.location.host}`;

/**
 *
 * @param options {object}
 * @param options.messages {object} Key-value object with translations.
 */
export default function tinymceAdapter(options) {
	// In the main time, we will undo the AMD related changes but leave the code
	// commented out, to better show our intentions and work progress.

	// Define theme.
	const editorTemplate = AtlassianTheme();
	window.tinymce.create('tinymce.themes.ConfluenceTheme', editorTemplate.ThemeOptions);
	window.tinymce.ThemeManager.add('atlassian', window.tinymce.themes.ConfluenceTheme);

	window.requestAnimFrame = window.requestAnimationFrame || window.webkitRequestAnimationFrame;

	// This function is a duplication of
	// confluence-editor/src/main/resources/tinymce3/settings/tinymce-default-settings.js
	//
	// When CQ-1043 gets fixed and we put AMD back, this method should be removed, and
	// we should be back to use the "settings" require dependency.
	//
	function getDefaultSettings() {
		const contextPath = CONTEXT_PATH;

		// all these plugins are enabled for page editor, page/inline comment editor.
		// if you want to enable plugin just for one kind of editor, add it in profiles.js
		const plugins = [
			// please be aware that reordering this list may
			// not be such a good idea
			'compat3x',
			'auiwindowmanager',
			'table',
			'emotions',
			'confluence',
			'macroplaceholder',
			'customtoolbar',
			'insertwikimarkup',
			'propertypanel',
			'keyboardshortcuts',
			'confmonospace',
			'confcharmap',
			'lists',
			'conflist',
			'tasklist',
			'paste',
			'aePaste',
			'cursorTarget',
			'confluencecleanupplugin',
			'aeTable',
			'confluencepastetableplugin',
			'confluencepastemacroplugin',
			'pagelayoutplugin',
			'iosediting',
			'autoconvert',
			'confluencelist',
			'deletecommand',
			'draggable',
			'confluencesortabletablesplugin',
			'textplaceholders',
			'linkbrowser',
			'insertfiles',
			'tableAnalytics',
			'aetextpattern',
			'persit_scroll_position',
			'confindentation',
			'textColorPlugin',
		];

		if (isDarkFeatureEnabled_DO_NOT_USE('confluence.table.resizable')) {
			plugins.push('tableContentWrapper');
		}

		return {
			//atlassian specific settings
			autoresize_min_height: '100%', //default if not specified in AJS.Meta

			// general
			width: '100%',
			height: '100%',
			document_base_url: `${HOST}${CONTEXT_PATH}/`,
			plugins: plugins.join(','),

			// advanced theme params
			theme: 'atlassian',
			// event though we don't use the advanced theme to generate the toolbar, we need the controls registered in TinyMCE
			theme_advanced_buttons1:
				'formatselect,bold,italic,underline,strikethrough,forecolor,separator,' +
				'table,row_before,row_after,delete_row,col_before,col_after,delete_col,delete_table,separator,' +
				'bullist,numlist,tasklist,outdent,indent,blockquote,justifyleft,justifycenter,justifyright,justifyfull,separator,sup,sub,separator,' +
				'undo,redo,separator,confimage,conf_macro_browser,separator,code,customtoolbar,help,monospace,separator,searchreplace,linkbrowserButton',
			theme_advanced_toolbar_location: 'top',
			theme_advanced_toolbar_align: 'left',
			theme_advanced_resizing: false,
			theme_advanced_resize_horizontal: false,
			theme_advanced_statusbar_location: 'none',
			theme_advanced_path: false,
			theme_advanced_blockformats: 'p,h1,h2,h3,h4,h5,h6,pre,blockquote',

			// selectors for tinymce editors
			mode: 'specific_textareas',
			editor_selector: 'tinymce-editor',
			//selector: 'textarea.tinymce-editor',

			// layout settings
			//body_class: 'wiki-content',
			body_class: 'aui-theme-default mceContentBody wiki-content',
			popup_css: false,
			content_css: false,
			editor_css: false,

			// undo settings
			custom_undo_redo: true,
			custom_undo_redo_levels: 20,

			// confluence-specific settings
			context_path: contextPath,
			plugin_action_base_path: `${contextPath}/plugins/tinymce`,
			editor_plugin_action_base_path: `${contextPath}/plugins/editor/tinymce`,
			page_id: getOverride('page-id'),
			draft_type: null,
			form_name: getOverride('form-name'),
			space_key: encodeURI(getOverride('space-key')),
			confluence_popup_width: 620,
			confluence_popup_height: 550,
			editor_id: 'wysiwygTextarea',

			// CONFDEV-54756: Tinymce4 defaults to indent: simple, which adds \n to each tag, when we do a getContent, it pretty prints the console
			// when set to false getContent will look like 1 clump of text, this is the way we like it.  Adding \n in the content will
			// causes instances of tiny3 to render incorrectly
			indent: false,
			/**
			 * CONFDEV-54925: don't depend on tinymce TAB implementation,
			 * do it by ourselves in editor-plugins/aeTable/aeTable-extend.js
			 */
			table_tab_navigation: false,
			inline_boundaries: false,
			// CONFCLOUD-56300, synchrony will strip out padding, use margin
			indent_use_margin: true,
			// CONFDEV-57749
			end_container_on_empty_block: true,
		};
	}

	function getProfileByContentType(contentType, isNewPage) {
		if (contentType === 'template') {
			return Profiles.createProfileForTemplateEditor();
		} else if (contentType === 'comment') {
			return Profiles.createProfileForCommentEditor();
		} else {
			//page, blogpost
			const profileOptions = {
				versionComment: !isNewPage,
				notifyWatchers: !isNewPage,
			};
			return Profiles.createProfileForPageEditor(profileOptions);
		}
	}

	/**
	 * Initialises the TinyMCE editor. This can be run without waiting for DOM ready if the language pack variable.
	 * TinyMCELang is available before calling this function.
	 */
	const settings = getDefaultSettings();

	// alter settings for drafts
	if (settings.page_id == 0) {
		// please double check this var type if you remove the loose equality check
		settings.page_id = null;
		settings.draft_type = getOverride('draft-type');
	}

	// Settings that are derived from the DOM cannot be assumed to exist yet so defer retrieval of their values
	getBootstrapManager().addBeforeInitCallback((s) => {
		s.contentCssTags = window.document.querySelector('script[title="editor-css"]').innerHTML;
		// tinymce language 'en_GB' was changed to 'en-GB' locale string.
		s.language = getOverride('action-locale').replace('_', '-');
		/**
		 * Inline comment is executing AJS.Meta.set('min-editor-height', 80)
		 * which set value to a private variable of module 'confluence/meta', this module is not on SPA
		 * that's why we must use AJS.getOverride() to retrieve correct value
		 * Update: AJS.Meta.set has been overriden by override-ajs-meta.sjs so it's safe again.
		 */
		const minHeight = getOverride('min-editor-height');
		if (minHeight) {
			s.autoresize_min_height = minHeight;
		}
	});

	// this plugins will be added by default to any instance of the editor that gets created.
	// eventually we should remove them and make that every editor context opt-in for them
	const autocompleteEnabled = !(
		getOverride('atlassian-account-id') &&
		getOverride('confluence.prefs.editor.disable.autocomplete')
	);
	if (autocompleteEnabled) {
		settings.plugins += ',autocomplete,autocompletelink,autocompletemedia';
	}

	// Store locale and translations in settings so it can be used in tinymce-bootstrap:initialise() later.
	settings.locale =
		typeof options === 'object' && typeof options.locale !== 'undefined' ? options.locale : '';
	settings.messages =
		typeof options === 'object' && typeof options.messages === 'object'
			? options.messages
			: undefined;

	const autoStart = !!getOverride('auto-start'); // slow comment, edit page/blogpost and template
	if (autoStart) {
		/**
		 * currently this code path is never executed in SPA
		 */
		if (process.env.NODE_ENV !== 'production') {
			console.error(
				'confluence-react-components/Editor/resources/tinymce-adapter.js: this code path should not execut in SPA',
			);
		}
		trigger('rte-loaded', { editPermission: true }); // Editor loaded event for slow edit, so if autostart only

		const editorContent = window.document.getElementById(settings.editor_id);
		trigger('rte-initial-raw-content-ready', {
			title: getOverride('page-title') || getOverride('original-content-title') || '',
			editorContent: editorContent.value,
			confRev: getOverride('conf-revision'),
			syncRev: window.document.getElementById('syncRev').value,
			syncRevSource: getOverride('sync-revision-source'),
		});

		debugTime('confluence.editor');

		if (autocompleteEnabled) {
			settings.plugins += ',autocompletemacro';
		}

		const newPage = `${getOverride('page-id')}` === '0';
		const profile = getProfileByContentType(getOverride('content-type'), newPage);

		const contextPlugins = profile.plugins.join(',');
		if (contextPlugins) {
			settings.plugins += `,${contextPlugins}`;
		}

		getBootstrapManager().preInitialise(settings);
		getBootstrapManager().initialise();
		debugTimeEnd('confluence.editor');
	} else {
		// leave the editor ready to be quickly initialised
		// this is the case of quick edit, quick comments, or quick custom editor
		// We just use the default plugins. The caller must provide
		// any additional plugins to initialise the editor with on
		// the AJS.Rte.BootstrapManager.initialise() call

		// CQ will fall in this case as well, as it calls window.tinymce.init manually
		getBootstrapManager().preInitialise(settings);
	}
}
